import { ALERT_SIGNALS, type AlertSignal } from '@apm/contracts';

/**
 * 규칙 파일에 쓸 수 있는 값의 도움말 (24회차, 제안 9).
 *
 * 규칙을 화면에서 고치게 하려면 "무엇을 쓸 수 있는가"가 그 옆에 있어야 한다. 서버는
 * 이름 목록(`AlertRulesResult.signals`)만 주므로 뜻·단위·써 볼 만한 값은 화면이 들고 있는다.
 *
 * `condition`의 칸 이름(`gtMs`·`gtPct`·`gt`·`minCount`·`minDurationMs`)과 값 이름은 파일에
 * 그대로 적는 글자라 번역하지 않는다. 설명만 화면 말로 쓴다.
 *
 * `keyBy`는 26회차부터 목록으로도 적을 수 있다(`[service, txType]`). 아래 표는 그 목록에
 * 넣을 수 있는 값이다.
 */

export interface SignalHelp {
  signal: AlertSignal;
  /** 파일에 적는 조건 칸 */
  condition: string;
  /** 값의 단위 */
  unit: string;
  /** 무엇을 보는 값인지 한 줄 */
  what: string;
  /** 예제 파일(`alerts.example.yml`)의 시작값 */
  example: string;
  /** 상태를 쪼갤 수 있는 기준(`keyBy`) */
  keyBy: string;
}

/** 신호마다 한 줄. 순서는 계약의 `ALERT_SIGNALS`와 같다 */
const SIGNAL_HELP: Record<AlertSignal, SignalHelp> = {
  loop_p99: {
    signal: 'loop_p99',
    condition: 'gtMs',
    unit: 'ms',
    what: '한 프로세스가 다음 일을 집기까지 밀린 시간. 상위 1%다',
    example: 'gtMs: 200 · for: 30',
    keyBy: 'all · service · instance',
  },
  heap_used_pct: {
    signal: 'heap_used_pct',
    condition: 'gtPct',
    unit: '%',
    what: '프로세스가 쓸 수 있는 메모리 상한 대비 지금 쓰는 비율',
    example: 'gtPct: 70(경고) · 85(심각) · for: 120',
    keyBy: 'all · service · instance',
  },
  pool_waiting: {
    signal: 'pool_waiting',
    condition: 'gt',
    unit: '건',
    what: 'DB 커넥션을 못 얻어 기다리는 요청 수',
    example: 'gt: 2 · for: 15',
    keyBy: 'all · service · instance',
  },
  tx_error_rate: {
    signal: 'tx_error_rate',
    condition: 'gtPct + minCount',
    unit: '%',
    what: '창 안에서 정상으로 끝나지 않은 요청의 비율',
    example: 'gtPct: 5 · minCount: 20 · window: 60',
    keyBy: 'all · service · instance · txType · entry',
  },
  tx_p99: {
    signal: 'tx_p99',
    condition: 'gtMs + minCount',
    unit: 'ms',
    what: '창 안 요청의 응답시간. 느린 쪽 상위 1%다',
    example: 'gtMs: 3000 · minCount: 20 · window: 300',
    keyBy: 'all · service · instance · txType · entry',
  },
  sql_error: {
    signal: 'sql_error',
    condition: 'minCount',
    unit: '건',
    what: '창 안에서 SQL 오류가 난 요청 수',
    example: 'minCount: 5 · window: 300',
    keyBy: 'all · service · instance · txType · entry',
  },
  db_blocked: {
    signal: 'db_blocked',
    condition: 'minDurationMs',
    unit: 'ms',
    what: '다른 세션에 막혀 기다리는 DB 세션이 있는 시간',
    example: 'minDurationMs: 3000',
    keyBy: 'all · database · appName',
  },
  db_idle_in_tx: {
    signal: 'db_idle_in_tx',
    condition: 'minDurationMs',
    unit: 'ms',
    what: '트랜잭션을 열어 둔 채 쉬는 DB 세션이 있는 시간',
    example: 'minDurationMs: 60000',
    keyBy: 'all · database · appName',
  },
  db_long_query: {
    signal: 'db_long_query',
    condition: 'minDurationMs',
    unit: 'ms',
    what: 'DB에서 아직 도는 중인 쿼리의 경과 시간',
    example: 'minDurationMs: 30000',
    keyBy: 'all · database · appName',
  },
  profile_captured: {
    signal: 'profile_captured',
    condition: '없음 (condition: {})',
    unit: '—',
    what: 'CPU 기록을 떴다는 사실 자체를 알린다. 경보로 남지 않는다',
    example: 'notifyOnResolve: false',
    keyBy: 'all · service · instance',
  },
  instance_gone: {
    signal: 'instance_gone',
    condition: '없음 (condition: {})',
    unit: '—',
    what: '1초 신호를 보내던 인스턴스가 조용해져 목록에서 빠졌다. 트레이스만 보내는 앱은 알리지 않는다',
    example: 'severity: critical · keyBy: instance',
    keyBy: 'all · service · instance',
  },
  instance_restarted: {
    signal: 'instance_restarted',
    condition: '없음 (condition: {})',
    unit: '—',
    what: '같은 이름으로 다른 프로세스가 올라왔거나, 사라졌던 인스턴스가 다시 나타났다',
    example: 'severity: warning · keyBy: instance',
    keyBy: 'all · service · instance',
  },
  server_error: {
    signal: 'server_error',
    condition: '없음 (condition: {})',
    unit: '—',
    what: '관제 서버 자신이 오류(ERROR) 로그를 냈다. 경고는 알리지 않는다. 서비스가 없어 관리자만 본다',
    example: 'severity: critical · cooldownMs: 600000',
    keyBy: 'all',
  },
};

/** 표에 그리는 순서. 서버가 준 이름 중 아는 것만, 계약 순서대로 */
export function signalHelpRows(signals: readonly string[]): SignalHelp[] {
  const known = new Set(signals);
  return ALERT_SIGNALS.filter((signal) => known.has(signal)).map((signal) => SIGNAL_HELP[signal]);
}
